import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  POPUP_V2_BLOCK_TYPES,
  getPopupV2CountdownState,
  getPopupV2MediaUrl,
  getVisiblePopupV2Blocks,
  resolvePopupV2Layout,
  resolvePopupV2LauncherTone,
  sanitizePopupV2Html,
} from "./popupV2Utils";

const DIALOG_ID = "fpep-popup-v2-dialog";
const MINIMIZE_ANIMATION_MS = 320;

const getLanguageLabels = (language = "me") => {
  const english = String(language || "").toLowerCase().startsWith("en");
  return english
    ? { days: "Days", hours: "Hours", minutes: "Min", seconds: "Sec", close: "Close notification", open: "Open notification" }
    : { days: "Dana", hours: "Sati", minutes: "Min", seconds: "Sek", close: "Zatvori obavještenje", open: "Otvori obavještenje" };
};

const getFocusableElements = (container) => {
  if (!container) return [];
  return Array.from(container.querySelectorAll('a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'))
    .filter((element) => !element.hasAttribute("hidden"));
};

function TextBlock({ block }) {
  const safeHtml = useMemo(() => sanitizePopupV2Html(block.bodyHtml), [block.bodyHtml]);
  return (
    <div className="fpep-popup-v2__text-block">
      {block.eyebrow && <span className="fpep-popup-v2__eyebrow">{block.eyebrow}</span>}
      {block.title && <h2>{block.title}</h2>}
      {safeHtml && <div className="fpep-popup-v2__rich-text" dangerouslySetInnerHTML={{ __html: safeHtml }} />}
    </div>
  );
}

function ImageBlock({ block }) {
  const src = getPopupV2MediaUrl(block.media);
  if (!src) return null;
  const ratio = String(block.aspectRatio || "original").replace(":", "-");
  const image = (
    <figure className={`fpep-popup-v2__image fpep-popup-v2__image--${ratio} fpep-popup-v2__image--${block.fit || "cover"}`}>
      <img src={src} alt={block.alt || block.media?.alt || ""} />
      {block.caption && <figcaption>{block.caption}</figcaption>}
    </figure>
  );

  if (!block.href) return image;
  return (
    <a
      className="fpep-popup-v2__media-link"
      href={block.href}
      target={block.newTab ? "_blank" : undefined}
      rel={block.newTab ? "noopener noreferrer" : undefined}
    >
      {image}
    </a>
  );
}

function CtaBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  return (
    <div className="fpep-popup-v2__cta-group">
      {items.map((item) => (
        <a
          key={item.id || `${item.label}-${item.url}`}
          className={`fpep-popup-v2__cta fpep-popup-v2__cta--${item.variant || "primary"}`}
          href={item.url}
          target={item.newTab ? "_blank" : undefined}
          rel={item.newTab ? "noopener noreferrer" : undefined}
        >
          {item.label}
        </a>
      ))}
    </div>
  );
}

function YoutubeBlock({ block }) {
  const [playing, setPlaying] = useState(false);
  const playback = block.playback === "youtube" ? "youtube" : "inline";
  const thumbnailUrl = block.thumbnailUrl || "";
  const embedUrl = block.embedUrl || "";
  const youtubeUrl = block.youtubeUrl || block.url || "";

  if (playing && playback === "inline" && embedUrl) {
    return (
      <div className="fpep-popup-v2__youtube-frame">
        <iframe
          src={`${embedUrl}?autoplay=1&rel=0`}
          title={block.title || "YouTube"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (!thumbnailUrl) return null;

  const copy = (
    <>
      <img src={thumbnailUrl} alt={block.title || "YouTube thumbnail"} />
      <span className="fpep-popup-v2__youtube-play" aria-hidden="true">▶</span>
      {(block.title || block.caption) && (
        <span className="fpep-popup-v2__youtube-copy">
          {block.title && <strong>{block.title}</strong>}
          {block.caption && <small>{block.caption}</small>}
        </span>
      )}
    </>
  );

  if (playback === "youtube") {
    return (
      <a className="fpep-popup-v2__youtube" href={youtubeUrl} target="_blank" rel="noopener noreferrer">
        {copy}
      </a>
    );
  }

  return (
    <button type="button" className="fpep-popup-v2__youtube" onClick={() => setPlaying(true)} aria-label={block.title || "Play YouTube video"}>
      {copy}
    </button>
  );
}

function CountdownBlock({ block, now, language }) {
  const labels = getLanguageLabels(language);
  const state = getPopupV2CountdownState(block, now);
  if (!state.valid) return null;

  const totalSeconds = Math.floor(state.remaining / 1000);
  const values = [
    [labels.days, Math.floor(totalSeconds / 86400)],
    [labels.hours, Math.floor((totalSeconds % 86400) / 3600)],
    [labels.minutes, Math.floor((totalSeconds % 3600) / 60)],
    [labels.seconds, totalSeconds % 60],
  ];

  return (
    <div className={`fpep-popup-v2__countdown ${state.expired ? "is-expired" : ""}`} aria-live="polite">
      {block.eyebrow && <span className="fpep-popup-v2__eyebrow">{block.eyebrow}</span>}
      {block.title && <strong className="fpep-popup-v2__countdown-title">{block.title}</strong>}
      {!state.expired ? (
        <div className="fpep-popup-v2__countdown-grid">
          {values.map(([label, value]) => (
            <span key={label}><b>{String(value).padStart(2, "0")}</b><small>{label}</small></span>
          ))}
        </div>
      ) : (
        block.expiredText ? <p>{block.expiredText}</p> : null
      )}
    </div>
  );
}

function PopupBlock({ block, now, language }) {
  switch (block.type) {
    case POPUP_V2_BLOCK_TYPES.TEXT:
      return <TextBlock block={block} />;
    case POPUP_V2_BLOCK_TYPES.IMAGE:
      return <ImageBlock block={block} />;
    case POPUP_V2_BLOCK_TYPES.CTA:
      return <CtaBlock block={block} />;
    case POPUP_V2_BLOCK_TYPES.YOUTUBE:
      return <YoutubeBlock block={block} />;
    case POPUP_V2_BLOCK_TYPES.COUNTDOWN:
      return <CountdownBlock block={block} now={now} language={language} />;
    default:
      return null;
  }
}


const LAUNCHER_LOGO_SRC = "/assets/img/logo/logobezteksta.png";

function LauncherIcon({ name = "building" }) {
  const common = {
    viewBox: "0 0 24 24",
    className: "fpep-popup-v2-launcher__icon",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: "false",
  };

  if (name === "graduation") return <svg {...common}><path d="M3 9.25 12 5l9 4.25L12 13.5 3 9.25Z"/><path d="M6.5 11.2v4.05c0 1.45 2.45 2.75 5.5 2.75s5.5-1.3 5.5-2.75V11.2"/><path d="M21 9.25v5"/></svg>;
  if (name === "book") return <svg {...common}><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5v-16Z"/><path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5v-16Z"/></svg>;
  if (name === "calendar") return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4M17 3v4M3 10h18"/><path d="M8 14h3M13 14h3M8 17h3"/></svg>;
  if (name === "info") return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 10v6M12 7h.01"/></svg>;
  if (name === "megaphone") return <svg {...common}><path d="M4 13v-2l11-5v12L4 13Z"/><path d="M15 9h2a3 3 0 0 1 0 6h-2M6 13l2 6h3l-1.5-5"/></svg>;
  if (name === "globe") return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.3 2.5 3.5 5.5 3.5 9S14.3 18.5 12 21c-2.3-2.5-3.5-5.5-3.5-9S9.7 5.5 12 3Z"/></svg>;
  if (name === "sparkles") return <svg {...common}><path d="m12 3 1.2 3.3L16.5 7.5l-3.3 1.2L12 12l-1.2-3.3L7.5 7.5l3.3-1.2L12 3Z"/><path d="m18.5 13 0.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2ZM5.5 14l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8Z"/></svg>;
  return <svg {...common}><path d="M4 19h16"/><path d="M6 17V9h12v8"/><path d="M8.5 17v-5M12 17v-5M15.5 17v-5"/><path d="M4.5 9 12 5l7.5 4H4.5Z"/></svg>;
}

const PopupV2 = ({ campaign, mode = "modal", language = "me", onClose, onRestore, onCampaignHidden }) => {
  const [now, setNow] = useState(Date.now());
  const [isMinimizing, setIsMinimizing] = useState(false);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const launcherRef = useRef(null);
  const previousModeRef = useRef(null);
  const labels = useMemo(() => getLanguageLabels(language), [language]);

  useEffect(() => {
    const hasCountdown = campaign?.blocks?.some((block) => block.type === POPUP_V2_BLOCK_TYPES.COUNTDOWN);
    if (!hasCountdown) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [campaign?.blocks]);

  const visibility = useMemo(
    () => getVisiblePopupV2Blocks(campaign?.blocks || [], now),
    [campaign?.blocks, now],
  );

  useEffect(() => {
    if (visibility.hideCampaign || visibility.blocks.length === 0) onCampaignHidden?.();
  }, [onCampaignHidden, visibility.blocks.length, visibility.hideCampaign]);

  const handleMinimize = useCallback(() => {
    if (isMinimizing) return;
    const reduceMotion = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setIsMinimizing(true);
    window.setTimeout(() => {
      onClose?.();
      setIsMinimizing(false);
    }, reduceMotion ? 0 : MINIMIZE_ANIMATION_MS);
  }, [isMinimizing, onClose]);

  useEffect(() => {
    const previousMode = previousModeRef.current;
    if (mode === "modal" && previousMode !== "modal") {
      window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    } else if (mode === "launcher" && previousMode === "modal") {
      window.requestAnimationFrame(() => launcherRef.current?.focus());
    }
    previousModeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    if (mode !== "modal") return undefined;
    const handleKeyboard = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleMinimize();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusableElements(dialogRef.current);
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [handleMinimize, mode]);

  if (visibility.hideCampaign || visibility.blocks.length === 0) return null;

  const presentation = campaign.presentation;
  const resolved = resolvePopupV2Layout(presentation.layout, visibility.blocks);
  const renderBlock = (block) => <PopupBlock key={block.id} block={block} now={now} language={language} />;

  if (mode === "launcher") {
    if (!campaign.behavior.showLauncher) return null;
    const launcherTone = resolvePopupV2LauncherTone(presentation);
    const launcherVisual = presentation.launcherVisual || "icon";
    return (
      <button
        ref={launcherRef}
        type="button"
        className={`fpep-popup-v2-launcher fpep-popup-v2-launcher--${launcherTone} fpep-popup-v2-launcher--visual-${launcherVisual}`}
        onClick={onRestore}
        aria-label={campaign.accessibilityLabel || campaign.launcherLabel || labels.open}
        aria-controls={DIALOG_ID}
        aria-haspopup="dialog"
      >
        {launcherVisual !== "none" && (
          <span className="fpep-popup-v2-launcher__mark" aria-hidden="true">
            {launcherVisual === "logo"
              ? <img src={LAUNCHER_LOGO_SRC} alt="" />
              : <LauncherIcon name={presentation.launcherIcon} />}
          </span>
        )}
        <span className="fpep-popup-v2-launcher__copy">
          <strong>{campaign.launcherLabel || labels.open}</strong>
          {campaign.launcherSubtitle && <span>{campaign.launcherSubtitle}</span>}
        </span>
      </button>
    );
  }

  return (
    <div
      className={`fpep-popup-v2-overlay ${isMinimizing ? "is-minimizing" : ""}`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleMinimize();
      }}
    >
      <section
        ref={dialogRef}
        id={DIALOG_ID}
        className={`fpep-popup-v2 fpep-popup-v2--${presentation.theme} fpep-popup-v2--${presentation.size} fpep-popup-v2--${resolved.layout} fpep-popup-v2--align-${presentation.alignment} fpep-popup-v2--media-${presentation.mediaMode} ${isMinimizing ? "is-minimizing" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={campaign.accessibilityLabel || campaign.launcherLabel || "FPEP"}
        tabIndex="-1"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="fpep-popup-v2__close"
          onClick={handleMinimize}
          aria-label={labels.close}
        >
          ×
        </button>

        {resolved.layout === "centered" ? (
          <div className="fpep-popup-v2__content">{visibility.blocks.map(renderBlock)}</div>
        ) : resolved.layout === "media-top" ? (
          <>
            <div className="fpep-popup-v2__media-pane">{resolved.media && renderBlock(resolved.media)}</div>
            <div className="fpep-popup-v2__content">{resolved.content.map(renderBlock)}</div>
          </>
        ) : resolved.layout === "media-left" || resolved.layout === "media-right" ? (
          <div className="fpep-popup-v2__split">
            <div className="fpep-popup-v2__media-pane">{resolved.media && renderBlock(resolved.media)}</div>
            <div className="fpep-popup-v2__content">{resolved.content.map(renderBlock)}</div>
          </div>
        ) : (
          <div className="fpep-popup-v2__full-media">
            <div className="fpep-popup-v2__media-pane">{resolved.media && renderBlock(resolved.media)}</div>
            <div className="fpep-popup-v2__content">{resolved.content.map(renderBlock)}</div>
          </div>
        )}
      </section>
    </div>
  );
};

export default PopupV2;
